import defaultAvatar from "../../assets/sampleAvatar.png";
import { useContext, useEffect, useRef, useState } from "react";
import { CurrentUserContext } from "App";
import RoundedPurpleContainer from "components/profile/RoundedPurpleContainer";
import ConnectionsListModal from "components/profile/ConnectionsListModal";
import { useSearchParams, useLocation, useNavigate } from "react-router-dom";
import ActionButton from "components/buttons/ActionButton";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3080";

function resolveAvatar(avatarStr) {
  if (!avatarStr || avatarStr === "sampleAvatar.png") return defaultAvatar;
  return avatarStr;
}

// Rank badge colors: gold, silver, bronze, then purple for the rest
const RANK_COLORS = ["#F7D000", "#828282", "#D68230"];

function TopConnectionItem({ connection, rank }) {
  const bgColor = rank < 3 ? RANK_COLORS[rank] : "var(--main-purple)";
  return (
    <div className="p-2 d-flex align-items-center">
      <div className="d-flex align-items-center justify-content-center" style={{ minWidth: "2.5rem" }}>
        {rank < 3 ? (
          <ion-icon name="flame" style={{ fontSize: "1.8rem", color: bgColor }}></ion-icon>
        ) : (
          <span className="fw-bold" style={{ fontSize: "1.2rem", color: "var(--main-purple)" }}>
            {rank + 1}
          </span>
        )}
      </div>
      <div
        className="w-100 rounded d-flex align-items-center justify-content-between px-3 py-2"
        style={{ backgroundColor: bgColor, minHeight: "2.5rem" }}
      >
        <span className="fw-bold" style={{ color: "#000", fontSize: "1rem" }}>
          {connection.username}
        </span>
        <span style={{ color: "#000", fontSize: "0.85rem", opacity: 0.7 }}>
          {connection.interaction_count} interactions
        </span>
      </div>
    </div>
  );
}

function MemoryCard({ update }) {
  const hasMedia = update.daily_update_type === "media" && update.media_src;
  return (
    <div
      className="rounded-3 d-flex flex-column justify-content-end overflow-hidden"
      style={{
        backgroundColor: "var(--main-purple)",
        minHeight: "12rem",
        position: "relative",
        cursor: "pointer",
      }}
    >
      {hasMedia && (
        <img
          src={update.media_src}
          alt={update.title}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
          }}
        />
      )}
      <div
        className="p-2"
        style={{
          background: hasMedia
            ? "linear-gradient(transparent, rgba(0,0,0,0.7))"
            : "transparent",
          position: "relative",
          zIndex: 1,
        }}
      >
        <p
          className="mb-0 fw-bold text-white"
          style={{
            fontSize: "0.85rem",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {update.title}
        </p>
        {update.body && !hasMedia && (
          <p
            className="mb-0 text-white"
            style={{
              fontSize: "0.75rem",
              opacity: 0.8,
              overflow: "hidden",
              display: "-webkit-box",
              WebkitLineClamp: 3,
              WebkitBoxOrient: "vertical",
            }}
          >
            {update.body}
          </p>
        )}
        <div className="d-flex align-items-center gap-1 mt-1">
          <ion-icon name="heart" style={{ color: "#ff6b6b", fontSize: "0.75rem" }}></ion-icon>
          <span style={{ color: "#fff", fontSize: "0.7rem" }}>{update.number_of_likes}</span>
        </div>
      </div>
    </div>
  );
}

function TopConnectionsSection({ token, targetUserId }) {
  const [topConnections, setTopConnections] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const body = { limit: 5 };
    if (targetUserId) body.targetUserId = targetUserId;

    fetch(`${API_URL}/getTopConnections`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
    })
      .then((r) => r.json())
      .then((r) => {
        if (r.message === "success") {
          setTopConnections(r.topConnections || []);
        }
      })
      .catch((err) => console.error("Error fetching top connections:", err))
      .finally(() => setLoading(false));
  }, [token, targetUserId]);

  return (
    <div
      id="top-connections-area"
      className="w-100 d-flex flex-column align-items-center"
      style={{ maxWidth: "30rem", margin: "0 auto" }}
    >
      <div className="text-center mb-3">
        <span
          className="text-decoration-underline fw-bold"
          style={{ fontSize: "1.6rem" }}
        >
          Top Connections
        </span>
      </div>
      {loading ? (
        <div className="d-flex justify-content-center py-3">
          <div className="spinner-border text-light spinner-border-sm" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : topConnections.length === 0 ? (
        <p className="text-center" style={{ color: "var(--main-purple)", fontSize: "0.95rem" }}>
          No connections yet. Start connecting!
        </p>
      ) : (
        <div className="d-flex flex-column gap-1 w-100">
          {topConnections.map((conn, index) => (
            <TopConnectionItem key={conn.user_id} connection={conn} rank={index} />
          ))}
        </div>
      )}
    </div>
  );
}

function MemoriesSection({ token, targetUserId }) {
  const [memories, setMemories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const body = { limit: 50 };
    if (targetUserId) body.targetUserId = targetUserId;

    fetch(`${API_URL}/getUserDailyUpdates`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
    })
      .then((r) => r.json())
      .then((r) => {
        if (r.message === "success") {
          setMemories(r.dailyUpdates || []);
        }
      })
      .catch((err) => console.error("Error fetching memories:", err))
      .finally(() => setLoading(false));
  }, [token, targetUserId]);

  return (
    <div id="memories-area" className="w-100" style={{ maxWidth: "40rem", margin: "0 auto" }}>
      <header className="text-center mb-3">
        <h3 className="mb-0">
          Memories{" "}
          <span style={{ color: "var(--main-purple)", fontSize: "1.2rem" }}>
            {memories.length > 0 ? memories.length : ""}
          </span>
        </h3>
      </header>
      {loading ? (
        <div className="d-flex justify-content-center py-3">
          <div className="spinner-border text-light spinner-border-sm" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : memories.length === 0 ? (
        <p className="text-center" style={{ color: "var(--main-purple)", fontSize: "0.95rem" }}>
          No memories yet. Share your first daily update!
        </p>
      ) : (
        <section
          id="memories-component"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(4, 1fr)",
            gap: "0.75rem",
          }}
        >
          {memories.map((update) => (
            <MemoryCard key={update.daily_update_id} update={update} />
          ))}
        </section>
      )}
      <style>{`
        @media (max-width: 768px) {
          #memories-component {
            grid-template-columns: repeat(2, 1fr) !important;
          }
        }
      `}</style>
    </div>
  );
}

export default function ProfilePage() {
  const { currentUser, setCurrentUser } = useContext(CurrentUserContext);
  const [numberOfConnections, setNumberOfConnections] = useState(0);
  const [numberOfFollowers, setNumberOfFollowers] = useState(0);
  const [numberOfFollowing, setNumberOfFollowing] = useState(0);
  const [followed, setFollowed] = useState(false);
  const [preview, setPreview] = useState(null);
  const [otherUserAvatar, setOtherUserAvatar] = useState(null);
  const [connectionsModal, setConnectionsModal] = useState(null); // "followers" | "connections" | "following" | null
  const [searchParams] = useSearchParams();
  const uploadFileRef = useRef(null);
  const location = useLocation();

  const isOtherUser =
    searchParams.get("username") &&
    searchParams.get("username") !== currentUser.username;
  const searchPersonID = location.state?.searchPersonID;

  // sync with server
  useEffect(() => {
    const searchedUsername = searchParams.get("username");
    let endpoint = `${API_URL}/`;
    if (searchedUsername && searchedUsername !== currentUser.username) {
      endpoint += "getSearchedUserConnectionsCount";
    } else {
      endpoint += "getNumberOfEachConnectionTypeForCurrentUser";
    }

    fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${currentUser.token}`,
      },
      body:
        searchedUsername && searchedUsername !== currentUser.username
          ? JSON.stringify({ searchPersonID: location.state?.searchPersonID })
          : null,
    })
      .then((r) => r.json())
      .then((r) => {
        if (r.message === "success") {
          setNumberOfConnections(r.connections);
          setNumberOfFollowers(r.followers);
          setNumberOfFollowing(r.following);
          if (r.isFollowed) {
            setFollowed(r.isFollowed);
          }
          if (r.avatar) {
            setOtherUserAvatar(r.avatar);
          }
        } else {
          console.log(r.message);
        }
      })
      .catch((err) => console.error(err));
  }, [searchParams, currentUser.token, location.state?.searchPersonID]);

  function follow() {
    fetch(`${API_URL}/updateUserConnections`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${currentUser.token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        username: searchParams.get("username"),
        status: "followed",
      }),
    })
      .then((r) => r.json())
      .then((r) => {
        if (r.message === "success") {
          setFollowed(true);
        }
      })
      .catch((err) => console.error(err));
  }

  function unfollow() {
    fetch(`${API_URL}/updateUserConnections`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${currentUser.token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        username: searchParams.get("username"),
        status: "unfollowed",
      }),
    })
      .then((r) => r.json())
      .then((r) => {
        if (r.message === "success") {
          setFollowed(false);
        }
      })
      .catch((err) => console.error(err));
  }

  const handleFileChange = async (e) => {
    if (!e.target.files || e.target.files.length === 0) return;

    const file = e.target.files[0];
    const extension = file.name.split(".").pop().toLowerCase();
    if (!["jpg", "png", "jpeg"].includes(extension)) {
      alert("You must select a JPG, JPEG, or PNG file.");
      return;
    }

    // Read file as base64
    const reader = new FileReader();
    reader.onload = async () => {
      const imageBase64 = reader.result;
      setPreview(imageBase64);

      try {
        const res = await fetch(`${API_URL}/uploadAvatar`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${currentUser.token}`,
          },
          body: JSON.stringify({ imageBase64 }),
        });
        const data = await res.json();
        if (data.message === "success") {
          const updatedUser = { ...currentUser, avatar: data.avatar };
          setCurrentUser(updatedUser);
          localStorage.setItem("currentUser", JSON.stringify(updatedUser));
        }
      } catch (err) {
        console.error("Avatar upload error:", err);
      }
    };
    reader.readAsDataURL(file);
  };

  return isOtherUser ? (
    // DISPLAY THE OTHER USERS PROFILE IF SEARCH PARAMS EXIST
    <div className="profile-layout">
      {/* PERSON'S PROFILE */}
      <div
        id="profile-area"
        className="d-flex flex-column align-items-center"
      >
        {/* AVATAR */}
        <div
          className="mb-4 position-relative"
          style={{ maxWidth: "10rem" }}
        >
          <img
            src={resolveAvatar(otherUserAvatar || location.state?.searchPersonAvatar)}
            alt="User Avatar"
            className="rounded-circle w-100"
            style={{ aspectRatio: "1", objectFit: "cover" }}
          />
        </div>

        {/* BOTTOM AREA OF PERSON'S PROFILE */}
        <div className="d-flex justify-content-center align-items-center gap-2 flex-wrap">
          {/* NAME AND DESCRIPTION */}
          <div>
            <span className="fw-bold profile-username" style={{ fontSize: "2.4rem" }}>
              {searchParams.get("username") || "???"}
            </span>
          </div>

          {/* TROPHIES */}
          <div
            className="d-flex align-items-center justify-content-center p-2 rounded-2 gap-1"
            style={{
              backgroundColor: "var(--trophy-bg)",
              maxWidth: "6rem",
              maxHeight: "3rem",
            }}
          >
            <div className="d-flex align-items-center justify-content-center">
              <ion-icon
                name="trophy"
                style={{ color: "var(--trophy-yellow)", fontSize: "2rem" }}
              ></ion-icon>
            </div>
            <div>
              <span
                className="fw-bold"
                style={{ color: "var(--trophy-yellow)" }}
              >
                60
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Followers / Following Area */}
      <div id="following-area" className="d-flex justify-content-center gap-3">
        <RoundedPurpleContainer
          label={"Followers"}
          number={numberOfFollowers}
          onClick={() => setConnectionsModal("followers")}
        />
        <RoundedPurpleContainer
          label={"Following"}
          number={numberOfFollowing}
          onClick={() => setConnectionsModal("following")}
        />
      </div>

      {/* Follow or Unfollow button */}
      {searchParams.get("username") !== currentUser.username ? (
        <ActionButton
          buttonText={followed ? "Unfollow" : "Follow"}
          buttonValue={followed ? "unfollow" : "follow"}
          buttonAction={followed ? unfollow : follow}
        />
      ) : null}

      {/* TOP CONNECTIONS AREA */}
      <TopConnectionsSection
        token={currentUser.token}
        targetUserId={searchPersonID}
      />

      {/* MEMORIES SECTION */}
      <MemoriesSection
        token={currentUser.token}
        targetUserId={searchPersonID}
      />

      {connectionsModal && (
        <ConnectionsListModal
          token={currentUser.token}
          targetUserId={searchPersonID}
          initialTab={connectionsModal}
          onClose={() => setConnectionsModal(null)}
          isOwnProfile={false}
        />
      )}
    </div>
  ) : (
    // DISPLAY CURRENT USERS PROFILE
    <div className="profile-layout">
      {/* PERSON'S PROFILE */}
      <div
        id="profile-area"
        className="d-flex flex-column align-items-center"
      >
        {/* AVATAR */}
        <div
          className="mb-4 position-relative"
          style={{ maxWidth: "10rem" }}
        >
          <img
            src={preview || resolveAvatar(currentUser?.avatar)}
            alt="Your Avatar"
            className="rounded-circle w-100"
            style={{ aspectRatio: "1", objectFit: "cover" }}
          />
          {/* SVG CONTAINER */}
          <div
            className="position-absolute translate-middle d-flex align-items-center justify-content-center rounded-circle"
            style={{
              backgroundColor: "var(--main-purple)",
              padding: ".3rem",
              top: "80%",
              left: "80%",
            }}
            onClick={() => uploadFileRef.current.click()}
          >
            <ion-icon
              name="pencil"
              className="w-100"
              style={{
                fontSize: "1.2rem",
                color: "var(--main-black)",
              }}
            ></ion-icon>
          </div>
        </div>

        {/* BOTTOM AREA OF PERSON'S PROFILE */}
        <div className="d-flex justify-content-center align-items-center gap-2 flex-wrap">
          {/* NAME AND DESCRIPTION */}
          <div>
            <span className="fw-bold profile-username" style={{ fontSize: "2.4rem" }}>
              {currentUser?.username}
            </span>
          </div>

          {/* TROPHIES */}
          <div
            className="d-flex align-items-center justify-content-center p-2 rounded-2 gap-1"
            style={{
              backgroundColor: "var(--trophy-bg)",
              maxWidth: "6rem",
              maxHeight: "3rem",
            }}
          >
            <div className="d-flex align-items-center justify-content-center">
              <ion-icon
                name="trophy"
                style={{ color: "var(--trophy-yellow)", fontSize: "2rem" }}
              ></ion-icon>
            </div>
            <div>
              <span
                className="fw-bold"
                style={{ color: "var(--trophy-yellow)" }}
              >
                60
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Connections / Following Area */}
      <div
        id="following-area"
        className="d-flex align-items-center justify-content-evenly gap-3"
      >
        <RoundedPurpleContainer
          label={"Followers"}
          number={numberOfFollowers}
          onClick={() => setConnectionsModal("followers")}
        />
        <RoundedPurpleContainer
          label={"Following"}
          number={numberOfFollowing}
          onClick={() => setConnectionsModal("following")}
        />
      </div>

      {/* TOP CONNECTIONS AREA */}
      <TopConnectionsSection token={currentUser.token} />

      {/* MEMORIES SECTION */}
      <MemoriesSection token={currentUser.token} />

      <input
        type="file"
        accept=".png, .jpg, .jpeg"
        ref={uploadFileRef}
        style={{ display: "none" }}
        onChange={handleFileChange}
      />

      {connectionsModal && (
        <ConnectionsListModal
          token={currentUser.token}
          targetUserId={isOtherUser ? searchPersonID : currentUser.user_id}
          initialTab={connectionsModal}
          onClose={() => setConnectionsModal(null)}
        />
      )}
    </div>
  );
}
